import { Check, CircleCheck, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';

import { Button } from '@askable/ui/core/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@askable/ui/core/dialog';
import { InfoAlert } from '@askable/ui/core/info-alert';
import { toast } from '@askable/ui/core/sonner';

import { analytics } from 'lib/analytics';

import { RequestDemo } from './data/RequestDemo.mutation';

const ANNOUNCEMENT_URL = 'https://www.askable.com/blog/new-pricing-announcement';

type CreditPurchasingDisabledModalProps = {
  open: boolean;
  onClose: () => void;
};

const FEATURE_BULLETS = ['methods', 'seats', 'participants'] as const;

export const CreditPurchasingDisabledModal = ({ open, onClose }: CreditPurchasingDisabledModalProps) => {
  const { t } = useTranslation();
  const [{ fetching }, requestDemo] = useMutation(RequestDemo);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleClose = () => {
    // Reset submission state so a reopened modal shows the CTA again — the modal stays
    // mounted in appProvider, so state persists across open/close cycles otherwise.
    setIsSubmitted(false);
    onClose();
  };

  const handleRequestDemo = async () => {
    // Segment stays client-side for product analytics; the backend mutation owns
    // reliable delivery of the lead to the sales pipeline.
    void analytics.track('client.demo_request', { page: window.location.href });

    const { error } = await requestDemo({});
    if (error) {
      toast.error(t('sections.creditPurchasingDisabled.error'));
      return;
    }
    setIsSubmitted(true);
  };

  return (
    <Dialog open={open} onOpenChange={next => !next && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{t('sections.creditPurchasingDisabled.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 text-sm">
          <p className="font-medium">{t('sections.creditPurchasingDisabled.body.lead')}</p>
          <p>{t('sections.creditPurchasingDisabled.body.pitch')}</p>

          <ul className="flex flex-col gap-3">
            {FEATURE_BULLETS.map(bullet => (
              <li key={bullet} className="flex items-center gap-2">
                <Check className="size-4 shrink-0 text-success" />
                {t(`sections.creditPurchasingDisabled.bullets.${bullet}`)}
              </li>
            ))}
          </ul>

          <InfoAlert variant="info" width="full" description={t('sections.creditPurchasingDisabled.announcement.body')}>
            <a
              href={ANNOUNCEMENT_URL}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-info hover:underline focus:underline"
            >
              {t('sections.creditPurchasingDisabled.announcement.link')}
              <ExternalLink className="size-4 shrink-0" />
            </a>
          </InfoAlert>
        </div>

        <DialogFooter className="flex-row justify-end">
          {isSubmitted ? (
            <div className="flex h-8 items-center gap-1 text-sm">
              <CircleCheck className="size-4 shrink-0 text-success" />
              {t('sections.createStudy.upgrade.success')}
            </div>
          ) : (
            <Button
              type="button"
              variant="primary"
              // oxlint-disable-next-line no-misused-promises -- pre-existing async handler
              onClick={handleRequestDemo}
              disabled={fetching}
            >
              {t('sections.creditPurchasingDisabled.requestDemo')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
